<template>
    <div id="report-view"
         class="full-vue-page">
        <div class="container container--top-padding-for-mobile-header">
            <div class="mobile-header">
                <h2>{{ l('REPORTS.TITLE') }}</h2>
            </div>

            <div class="hidden-xs invert no-print">
                <h2>{{ l('REPORTS.TITLE') }}</h2>
            </div>

            <div v-if="!isOnline"
                 class="alert alert-danger">
                <i class="fa-solid fa-exclamation-circle"></i>
                {{ l('REPORTS.OFFLINE_ALERT') }}
            </div>

            <div v-if="showMissingTradingSessionError"
                 class="alert alert-danger">
                <p>
                    <i class="fa-solid fa-exclamation-circle"></i>
                    {{ l('REPORTS.TRADING_SESSION_MISSING_ERROR') }}
                </p>
                <p>
                    {{ l('REPORTS.TRADING_SESSION_MISSING_MESSAGE') }}
                </p>
                <p>
                    <button class="btn btn-primary btn-lg"
                            @click="reloadPage()">
                        <i class="fa-solid fa-clipboard-check"></i>&nbsp;{{ l('REPORTS.TRADING_SESSION_START_BTN') }}
                    </button>
                </p>
            </div>

            <div v-if="isOnline && currentDeviceTradingSessionCollection"
                 id="report-controls"
                 class="no-print">
                <!-- X-Read Button (if session selected and session is open) -->
                <button v-if="dataLoaded && !selectedTradingSession.endedAt"
                        id="x-read-btn"
                        class="btn btn-info btn-lg"
                        :disabled="isBusy"
                        @click="printReportHandler(xReadPrintTitle)">
                    <i class="fa-solid fa-print"></i>&nbsp;{{ l('REPORTS.X_READ_BTN') }}
                    <small>{{ l('REPORTS.X_READ_BTN_EXTRA_OPEN') }}</small>
                </button>
                <!-- Reprint Z-Read Button (if session selected and session is not open) -->
                <button v-else-if="dataLoaded"
                        id="x-read-btn"
                        class="btn btn-info btn-lg"
                        :disabled="isBusy"
                        @click="printReportHandler(zReadPrintTitle)">
                    <i class="fa-solid fa-print"></i>&nbsp;{{ l('REPORTS.Z_READ_BTN') }}
                    <small>{{ l('REPORTS.Z_READ_REPRINT_BTN_EXTRA') }}</small>
                </button>
                <!-- Disabled x-read button when no trading session selected -->
                <button v-else
                        id="x-read-btn"
                        class="btn btn-info btn-lg"
                        disabled>
                    <i class="fa-solid fa-print"></i>&nbsp;{{ l('REPORTS.X_READ_BTN') }}
                    <small>{{ l('REPORTS.X_READ_BTN_EXTRA_OPEN') }}</small>
                </button>

                <!-- Session Selector -->
                <button id="session-selector-btn"
                        class="btn btn-default btn-lg"
                        @click="showSessionSelector()">
                    <i class="fa-solid fa-caret-down"></i>
                    <template v-if="selectedTradingSession">
                        {{ dateFilter(selectedTradingSession.startedAt, true) }}
                        <small v-if="!selectedTradingSession.endedAt">{{ l('REPORTS.NOT_ENDED') }}</small>
                        <small v-else>- {{ dateFilter(selectedTradingSession.endedAt, true) }}</small>
                    </template>
                    <template v-else>
                        {{ l('REPORTS.SELECT_SESSION') }}
                    </template>
                </button>

                <!-- Z-Read Button (if session selected and session is open) -->
                <button v-if="dataLoaded && !selectedTradingSession.endedAt"
                        id="z-read-btn"
                        class="btn btn-primary btn-lg"
                        :disabled="isBusy || isReadOnly"
                        @click="zRead()">
                    <i class="fa-solid fa-clipboard-check"></i>&nbsp;{{ l('REPORTS.Z_READ_BTN') }}
                    <small>{{ l('REPORTS.Z_READ_BTN_EXTRA') }}</small>
                </button>
                <!-- Cash Up Button (if session selected and session is not open) -->
                <button v-else-if="dataLoaded"
                        class="btn btn-primary btn-lg"
                        :disabled="isBusy || isReadOnly || selectedTradingSession.tradingSessionStatusID === 3"
                        @click="cashUp()">
                    <i class="fa-solid fa-calculator"></i>&nbsp;{{ l('REPORTS.CASH_UP_BTN') }}
                    <small>{{ l('REPORTS.CASH_UP_BTN_EXTRA') }}</small>
                </button>
                <!-- Disabled z-read button when no session selected -->
                <button v-else
                        id="z-read-btn"
                        class="btn btn-primary btn-lg"
                        disabled>
                    <i class="fa-solid fa-clipboard-check"></i>&nbsp;{{ l('REPORTS.Z_READ_BTN') }}
                    <small>{{ l('REPORTS.Z_READ_BTN_EXTRA') }}</small>
                </button>
            </div>

            <div v-if="loading"
                 class="loading-spinner">
                <i class="fa-solid fa-spinner fa-spin"></i>
                <p>{{ l('REPORTS.LOADING') }}</p>
            </div>

            <div v-if="isOnline && waitingForOnlineTasks"
                 class="loading-spinner">
                <i class="fa-solid fa-spinner fa-spin"></i>
                <p>{{ l('REPORTS.WAITING_FOR_QUEUE') }}</p>
            </div>

            <div v-if="requestedTradingSessionLoadError"
                 class="alert alert-danger mt-1">
                <i class="fa-solid fa-exclamation-circle"></i>
                {{ l('REPORTS.FAILED_LOADING_REQUESTED') }}
            </div>

            <!-- Note - don't put a v-else here because the loading spinner may be shown while closing the session,
            and we need this element to print the report during this time too. -->
            <TradingSessionSummary v-if="isOnline && selectedTradingSession && summary"
                                   class="mt-1"
                                   :print-title="printTitle"
                                   :extended="currentDevice.extendedTradingSessionSummaries"
                                   :summary="summary"
                                   :trading-session="selectedTradingSession" />
        </div>
    </div>
</template>

<script>
import logFactory from '@/libraries/DigiTickets/Logging/logFactory';
import PaymentMethodRef from '@/lib/PaymentMethods/PaymentMethodRef';
import PrintJobOptions from '@/lib/Printing/PrintJobOptions';
import PrintType from '@/lib/Printing/PrintType';
import TradingSessionEvent from '@/lib/TradingSessions/TradingSessionEvent';
import TradingSessionEventType from '@/lib/TradingSessions/TradingSessionEventType';
import TradingSessionSummary from '@/epos/vue/pages/reports/components/TradingSessionSummary';
import { container } from '@/container';
import { dateFilter } from '@/vue/filters/dateFilter';
import { mapState } from 'vuex';
import { MODAL_NAMES__EPOS } from '@/epos/vue/modals/MODAL_NAMES__EPOS';
import { ModalCancelledError } from '@/vue/modals/ModalCancelledError';
import { suppressModalCancelledErrorOrReThrow } from '@/lib/Errors';
import { timeoutPromise } from '@/functions/functions';

export default {
    components: { TradingSessionSummary },

    data() {
        return {
            /**
             * @type {boolean}
             */
            loading: false,

            /**
             * @type {boolean}
             */
            waitingForOnlineTasks: false,

            /**
             * @type {ConsoleLogger}
             */
            log: null,

            /**
             * @type {TradingSessionCollection}
             */
            currentDeviceTradingSessionCollection: null,

            /**
             * @type {?TradingSession}
             */
            selectedTradingSession: null,

            /**
             * If there was an error loading the requested trading session.
             *
             * @type {boolean}
             */
            requestedTradingSessionLoadError: false,

            /**
             * @type {?TradingSessionSummary}
             */
            summary: null,

            /**
             * @type {boolean}
             */
            isPrinting: false,

            /**
             * Lang string printed at the top of the report.
             *
             * @type {string}
             */
            printTitle: null,
            xReadPrintTitle: 'REPORTS.X_READ_PRINT_TITLE',
            zReadPrintTitle: 'REPORTS.Z_READ_PRINT_TITLE',

            /**
             * @type {boolean}
             */
            sessionClosing: false,

            /**
             * Should we display an error to the user indicating that there is no active trading session.
             *
             * @type {boolean}
             */
            showMissingTradingSessionError: false,
        };
    },

    computed: {
        ...mapState([
            'isOnline',
            'currentDevice',
            'currentUser',
            'viewPrivileges',
        ]),

        ...mapState('onlineQueue', ['queuedTaskCount']),

        pathTradingSessionID() {
            // The path looks like : /reports/5
            // Extract the tradingSessionID from the path.
            // Note we are using Vue Router here!
            const path = this.$route.path;
            const parts = path.split('/');
            return parseInt(parts[2]);
        },

        dataLoaded() {
            return this.selectedTradingSession
                && this.summary;
        },

        /**
         * Is an operation currently in progress that should prevent interaction
         * (printing, closing session, etc.) at this point?
         * This includes waiting for the device to be online.
         *
         * @returns {boolean}
         */
        isBusy() {
            return this.loading
                || !this.isOnline
                || this.isPrinting
                || this.sessionClosing;
        },

        selectedSessionIsForCurrentDevice() {
            return this.selectedTradingSession?.deviceID === this.currentDevice.ID;
        },

        /**
         * Check if the current trading session is for a different device.
         *
         * @returns {boolean}
         */
        isReadOnly() {
            return !this.selectedSessionIsForCurrentDevice;
        },
    },

    watch: {
        // Watch for changes to the tradingSessionID in the URL.
        pathTradingSessionID: {
            async handler(tradingSessionID) {
                if (tradingSessionID) {
                    // A trading session ID is provided in the URL.
                    // Check if we already have that trading session loaded. If not, load it.
                    if (!this.selectedTradingSession || this.selectedTradingSession.ID !== tradingSessionID) {
                        await this.loadTradingSession(tradingSessionID);
                    }
                }
            },
            // Don't run this handler immediately on creation. Loading the initial session is handled in created().
            immediate: false,
        },

        queuedTaskCount: {
            handler(newCount, oldCount) {
                if (newCount > 0) {
                    this.waitingForOnlineTasks = true;
                    return;
                }

                this.waitingForOnlineTasks = false;
                if (newCount === 0 && oldCount > 0) {
                    // If the count has just reached zero that means tasks have just finished processing.
                    // If we have a selected trading session and a summary, refresh the summary.
                    // But not if we are currently closing the session - the session close process will handle
                    // refreshing the summary when any final float adjustments have been saved.
                    // Refreshing from here in this case could cause the rendered summary to disappear just as
                    // it is being printed.
                    if (this.selectedTradingSession && this.summary && !this.sessionClosing) {
                        console.log('Refreshing summary after online tasks completed');
                        this.loadTradingSessionSummary(this.selectedTradingSession);
                    }
                }
            },
            immediate: true,
        },
    },

    async created() {
        this.log = logFactory('ReportsView');

        if (!this.viewPrivileges.reports) {
            // The current user does not have access to reports.
            // Send them to the 403 page.
            container.navigationService.view403();
            return;
        }

        this.loading = true;

        // Load all the trading sessions for the current device to populate the session selector.
        await this.loadAllTradingSessions();

        if (this.pathTradingSessionID) {
            // If a tradingSessionID is provided on the URL on startup load that now.
            await this.loadTradingSession(this.pathTradingSessionID);
        } else {
            // If no trading session was provided in the URL, select the first one.
            if (this.currentDeviceTradingSessionCollection) {
                const openSessions = this.currentDeviceTradingSessionCollection.getOpen();
                if (openSessions.length > 0) {
                    console.log('No trading session selected, selecting the first open session', openSessions[0].ID);
                    await this.selectTradingSession(openSessions[0]);
                } else {
                    console.log('No open trading session. Prompt user to start new trading session.');
                    this.showMissingTradingSessionError = true;
                }
            }
        }
    },

    methods: {
        dateFilter,

        /**
         * Load the most recent trading sessions for the current device from the API.
         * They are sorted by start time in reverse order,
         * so the first one in the list will be the current trading session.
         */
        async loadAllTradingSessions() {
            this.loading = true;

            // Load the trading sessions only for this device. The trading session list modal will make a separate
            // request to load the sessions including other devices.
            return container.tradingSessionManager
                .getSessionsForDevice(this.currentDevice.ID)
                .then((currentDeviceTradingSessionCollection) => {
                    this.currentDeviceTradingSessionCollection = currentDeviceTradingSessionCollection;
                })
                .catch((err) => {
                    console.error('Error loading trading sessions', err);
                    container.toastFactory.errorTop('REPORTS.FAILED_LOADING');
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        /**
         * Load a single trading session by its ID from the API.
         *
         * @param {number} tradingSessionID
         *
         * @returns {Promise<TradingSession>}
         */
        async loadTradingSession(tradingSessionID) {
            // Clear the currently selected trading session and summary.
            await this.selectTradingSession(null);
            this.loading = true;

            try {
                const tradingSession = await container.tradingSessionManager.getSession(tradingSessionID);
                if (!tradingSession) {
                    // noinspection ExceptionCaughtLocallyJS
                    throw new Error('Trading session not found');
                }
                await this.selectTradingSession(tradingSession);
            } catch (err) {
                console.error('Error loading trading session', err);
                this.requestedTradingSessionLoadError = true;
            } finally {
                this.loading = false;
            }
        },

        async selectTradingSession(tradingSession) {
            this.requestedTradingSessionLoadError = false;
            this.summary = null;
            this.selectedTradingSession = tradingSession;

            if (tradingSession) {
                // Update the trading session ID in the URL.
                // If this is already the one in the URL this won't do anything.
                container.$location.path(`/reports/${tradingSession.ID}`).replace();

                await this.loadTradingSessionSummary(tradingSession);
            }
        },

        showSessionSelector() {
            container.vueModalFactory
                .open(
                    MODAL_NAMES__EPOS.TRADING_SESSIONS_LIST,
                    {
                        currentDeviceTradingSessionCollection: this.currentDeviceTradingSessionCollection,
                        allDevices: !this.selectedSessionIsForCurrentDevice,
                        selectedTradingSessionID: this.selectedTradingSession?.ID || null,
                    }
                )
                .then(async (selectedTradingSession) => {
                    return this.selectTradingSession(selectedTradingSession);
                })
                .catch(suppressModalCancelledErrorOrReThrow);
        },

        /**
         * @param {TradingSession} tradingSession
         *
         * @returns {Promise<TradingSessionSummary>}
         */
        async loadTradingSessionSummary(tradingSession) {
            console.debug('Loading trading session summary for', tradingSession.ID);

            this.summary = null;
            this.loading = true;

            // Got the latest trading session. Now get the summary for it.
            try {
                let summary = await container.tradingSessionManager.getSummary(tradingSession);
                summary = this.prepareFlexiVoucherData(summary);
                summary = this.prepareTillContentsData(summary);
                this.summary = summary;
            } catch (err) {
                console.error('Error loading trading session summary', err);
                this.summary = null;
                container.toastFactory.errorTop('REPORTS.FAILED_LOADING');
            } finally {
                this.loading = false;
            }
        },

        /**
         * Print an X-Read (or Z-Read reprint if already closed).
         *
         * @param {string} printTitle
         */
        async printReport(printTitle) {
            this.printTitle = printTitle;
            this.isPrinting = true;

            let tradingSessionEventType = null;

            switch (printTitle) {
                case 'REPORTS.X_READ_PRINT_TITLE':
                    tradingSessionEventType = TradingSessionEventType.X_READ_PRINTED;
                    break;
                case 'REPORTS.Z_READ_PRINT_TITLE':
                    tradingSessionEventType = TradingSessionEventType.Z_READ_PRINTED;
                    break;
            }

            if (tradingSessionEventType) {
                container.tradingSessionManager.saveEvent(
                    (new TradingSessionEvent(tradingSessionEventType))
                        .setOperatorID(this.currentUser.ID)
                )
                    .then(() => {});
            }

            await this.$nextTick(); // Wait for the UI to update.
            await timeoutPromise(10); // 10ms more for safety.

            let opts = new PrintJobOptions(
                PrintType.REPORT
            );

            const reportEl = document.getElementById('report');
            if (!reportEl) {
                throw new Error('#report element is not ready for printing.');
            }

            const promise = container.printer.printHtmlString(
                reportEl.outerHTML,
                opts
            );

            promise.finally(() => {
                this.printTitle = null;
                this.isPrinting = false;
            });

            return promise;
        },

        async zRead() {
            this.sessionClosing = true;

            let requiredFloatAdjustmentReasons = this.getRequiredFloatAdjustmentReasons();

            if (requiredFloatAdjustmentReasons.length > 0) {
                /* @type {FloatAdjustment[]} */
                let floatAdjustments = [];

                for (let requiredFloatAdjustmentReason of requiredFloatAdjustmentReasons) {
                    try {
                        // We are purposely awaiting in the loop because
                        // we want to ask the user questions sequentially.
                        let floatAdjustment = await this.displayAdjustFloatModal(requiredFloatAdjustmentReason);
                        floatAdjustments.push(floatAdjustment);
                    } catch (e) {
                        if (!(e instanceof ModalCancelledError)) {
                            this.log.error('Error displaying float adjustment modal', e);
                        }
                        floatAdjustments = [];
                        this.sessionClosing = false;
                        break;
                    }
                }

                if (floatAdjustments.length > 0) {
                    this.confirmCloseSession(floatAdjustments);
                }
            } else {
                this.log.log('Requested Z-Read (no prompt)');
                this.confirmCloseSession();
            }
        },

        cashUp() {
            return container.vueModalFactory
                .open(
                    MODAL_NAMES__EPOS.CASH_UP,
                    {
                        tradingSessionID: this.selectedTradingSession.ID,
                    }
                )
                .then((result) => {
                    if (result) {
                        container.toastFactory.successTop('CASH_UP.SUBMITTED');

                        // Update the selectedTradingSession to mark it as cashed up.
                        this.selectedTradingSession.tradingSessionStatusID = 3;
                        this.selectTradingSession(this.selectedTradingSession);
                    }
                })
                .then(suppressModalCancelledErrorOrReThrow);
        },

        /**
         * Return a promise containing the result of each float adjustment modal in turn.
         *
         * @param {FloatAdjustmentReason} floatAdjustmentReason
         *
         * @returns {Promise<FloatAdjustment>}
         */
        async displayAdjustFloatModal(floatAdjustmentReason) {
            return container.vueModalFactory
                .open(
                    MODAL_NAMES__EPOS.ADJUST_FLOAT,
                    {
                        mode: 'z-read',
                        reasons: [floatAdjustmentReason],
                    }
                )
                .then(({ floatAdjustment }) => {
                    return floatAdjustment;
                });
        },

        /**
         * Returns an array of the FloatAdjustmentReasons that are required to be prompted for
         * when the session is closed.
         *
         * @return {FloatAdjustmentReason[]}
         */
        getRequiredFloatAdjustmentReasons() {
            return container.userService.company.floatAdjustmentReasons.filter(
                (reason) => reason.requiredOnSessionClose
            );
        },

        /**
         * TODO: Combine this with the saveFloatAdjustmentTradingSessionEvent method in NavigationCtrl.
         * Could now use CashDrawerService.saveFloatAdjustmentTradingSessionEvent
         *
         * Save the float adjustment via the Trading Session manager.
         *
         * @param {FloatAdjustment} floatAdjustment
         */
        async persistFloatAdjustment(floatAdjustment) {
            let auditAmount = 0 - floatAdjustment.amount;
            let tradingSessionEvent = new TradingSessionEvent(TradingSessionEventType.CASH_OUT);

            // Setting the narrative means that if a client chooses to use a float adjustment
            // that requires a narrative it won't fail. If necessary we can always use an alternate modal or
            // modify the existing modal to add a narrative field.
            return container.tradingSessionManager.saveEvent(
                tradingSessionEvent
                    .setOperatorID(this.currentUser.ID)
                    .setAmount(auditAmount)
                    .setFloatAdjustmentReasonID(floatAdjustment.floatAdjustmentReason.ID)
                    .setNarrative(floatAdjustment.narrative)
            );
        },

        /**
         * Trigger the close session confirmation modal with appropriate message. If confirmation is given then send any
         * float adjustments that have just been made.
         *
         * @param {FloatAdjustment[]} [floatAdjustments]
         */
        confirmCloseSession(floatAdjustments) {
            let message = container.langService.getText('REPORTS.Z_READ_CONFIRM_MESSAGE');

            if (floatAdjustments) {
                message += '<br/>' + container.langService.getText('REPORTS.Z_READ_CONFIRM_FLOAT_ADJUSTMENTS');
                for (let floatAdjustment of floatAdjustments) {
                    message += '<br />'
                        + floatAdjustment.floatAdjustmentReason.reason
                        + ': '
                        + container.currencyService.getSymbol()
                        + floatAdjustment.amount;
                }
            }

            container.dialogService
                .confirm(null, { customHtml: message })
                .then(async (confirmed) => {
                    if (confirmed !== true) {
                        this.sessionClosing = false;
                        return;
                    }

                    // Give an indication that things are happening.
                    this.loading = true;

                    if (floatAdjustments !== undefined) {
                        for (const floatAdjustment of floatAdjustments) {
                            // Save each of the float adjustments and wait for them to complete.
                            await this.persistFloatAdjustment(floatAdjustment);
                        }
                    }

                    // Reload the summary now that the float adjustments have been saved.
                    await this.loadTradingSessionSummary(this.selectedTradingSession);

                    await this.closeSession();
                });
        },

        async closeSession() {
            // Confirmed - close the session.
            try {
                this.selectedTradingSession = await container.tradingSessionManager.closeSession(
                    this.selectedTradingSession
                );

                // Print the Z-Read.
                await this.printReportHandler(this.zReadPrintTitle);

                this.sessionClosing = false;

                await this.startNewSession();
            } catch (err) {
                console.error('Error closing trading session', err);
                container.toastFactory.error('REPORTS.FAILED_LOADING');
            }
        },

        /**
         * A simple method to wrap around printing, capture errors, and display a message to the user.
         * An error printing should have knock on effects like stopping a Trading Session from starting.
         *
         * @param {string} reportTitle
         *
         * @return {Promise<void>}
         */
        async printReportHandler(reportTitle) {
            try {
                await this.printReport(reportTitle);
            } catch (error) {
                container.toastFactory.error('REPORTS.PRINT_ERROR');
            }
        },

        async startNewSession() {
            // Start the new session.
            const tradingSession = await container.tradingSessionManager.openSession(
                container.currentDevice.device
            );

            container.currentDevice.setTradingSession(tradingSession);

            // Add the default float.
            await container.tradingSessionManager.saveEvent(
                (new TradingSessionEvent(TradingSessionEventType.CASH_FLOAT))
                    .setOperatorID(this.currentUser.ID)
                    .setAmount(tradingSession.device.defaultFloat)
            );

            // Refresh the list of trading sessions.
            await this.loadAllTradingSessions();

            // Select the first session which should be the new one.
            await this.selectTradingSession(this.currentDeviceTradingSessionCollection.getOpen()[0]);
        },

        /**
         * Process the summary to make any Flexi-Voucher data easily accessible in the template.
         *
         * @param summary
         * @return {*}
         */
        prepareFlexiVoucherData(summary) {
            const flexiVoucherData = summary.payments.filter((p) => {
                return p.paymentMethodRef === PaymentMethodRef.FLEXI_VOUCHER;
            });
            if (flexiVoucherData.length) {
                summary.flexiVoucherSummary = Object.assign({}, flexiVoucherData[0]);
            } else {
                summary.flexiVoucherSummary = {
                    tenderedTotalValue: 0,
                    changeGivenTotalValue: 0,
                    changeExcludingRefunds: 0,
                    salesTotalCount: 0,
                    salesTotalValue: 0,
                };
            }
            return summary;
        },

        /**
         * Process the summary to split Refunds and Change for the Cash Till Contents.
         *
         * @param {TradingSessionSummary} summary
         * @return {*}
         */
        prepareTillContentsData(summary) {
            const cashData = summary.payments.filter((p) => {
                return p.paymentMethodRef === PaymentMethodRef.CASH;
            });
            if (cashData.length) {
                summary.tillContents.changeExcludingRefunds = cashData[0].changeExcludingRefunds;
                summary.tillContents.refundsGiven = cashData[0].refundsTotalValue;
            } else {
                summary.tillContents.changeExcludingRefunds = 0;
                summary.tillContents.refundsGiven = 0;
            }
            return summary;
        },

        reloadPage() {
            // There used to be a very rare bug when a device became stuck without an active trading session.
            // We can now fix this by simply reloading the page but rather than doing this automatically it is better
            // to inform the user and then reload on a button click.
            window.location.reload();
        },
    },
};
</script>
